import { create } from 'zustand';
import api from '../api';

// Caches the current tenant's plan + feature_flags. Fetched once after login
// and re-fetched whenever the active company changes (super_admin switching
// tenants). Components subscribe via useFeature(key) below.
//
// `flags` is a plain object: boolean for capability flags, number-or-null for
// numeric quotas (null = unlimited). Until the first fetch settles, `loaded`
// is false and the UI should not lock anything — otherwise we'd flash a
// locked sidebar to every user on first paint.
export const useFeatureStore = create((set, get) => ({
  flags: {},
  planCode: null,
  planName: null,
  nextPlanCode: null,
  nextPlanName: null,
  loaded: false,
  loading: false,

  fetch: async () => {
    if (get().loading) return;
    set({ loading: true });
    try {
      const { data } = await api.get('/me/features');
      set({
        flags: data.flags || {},
        planCode: data.plan_code,
        planName: data.plan_name,
        nextPlanCode: data.next_plan_code,
        nextPlanName: data.next_plan_name,
        loaded: true,
        loading: false,
      });
    } catch (_) {
      // Soft-fail: leave loaded=false so the UI keeps showing everything
      // unlocked. The backend still enforces gates, so this is safe.
      set({ loading: false });
    }
  },

  clear: () => set({
    flags: {}, planCode: null, planName: null,
    nextPlanCode: null, nextPlanName: null,
    loaded: false, loading: false,
  }),
}));

// Hook: is a feature available on the current plan?
// Returns { enabled, locked, upgradeTo } so the caller can render badges
// without re-querying. `enabled` is true for unknown keys (fail-open) so a
// new nav item without a flag wired up doesn't disappear.
export const useFeature = (key) => {
  const { flags, loaded, nextPlanName } = useFeatureStore();
  if (!key) return { enabled: true, locked: false, upgradeTo: null };
  if (!loaded) return { enabled: true, locked: false, upgradeTo: null };
  const v = flags[key];
  if (v === undefined) return { enabled: true, locked: false, upgradeTo: null };
  const enabled = typeof v === 'boolean' ? v : v !== false;
  return { enabled, locked: !enabled, upgradeTo: enabled ? null : nextPlanName };
};
